import { DecimalPipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { OemDealerType } from '@app-model/oem-dealer';
import { DealTypeEnum, FuelEnum, SegmentLabel, TransmissionEnum } from '@app-model/vehicle';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { OpenService, VehicleFilter } from 'src/app/shared/services/open.service';
import {
  ActiveFilterChip,
  ActiveFilterChipKey,
  ActiveFilterLabelContext,
  EXCHANGE_FILTER_BUY_NOW_SUPPORTED,
  EXCHANGE_FILTER_DIFFERENTIAL_LABELS,
  FilterDetailBuildOptions,
  FilterDetailItem,
  formatReportStatusLabel,
  ExchangeFilterPageScope,
  getPublishedPeriodLabel,
  isDefaultDealTypeSelection,
  resolvePublishedPeriodKey,
} from './exchange-filter.model';

@Injectable({ providedIn: 'root' })
export class ExchangeFilterService {
  private readonly openService = inject(OpenService);
  private readonly decimalPipe = new DecimalPipe('pt-BR');

  buildActiveFilterChips(filter: VehicleFilter, context: ActiveFilterLabelContext = {}): ActiveFilterChip[] {
    if (!filter) {
      return [];
    }

    const chips: ActiveFilterChip[] = [];

    if (filter.searchQuery?.trim()) {
      chips.push(this.createChip({ type: 'search' }, filter.searchQuery.trim()));
    }

    filter.segments?.forEach((segment) => {
      const label = this.resolveSegmentLabel(segment);
      chips.push(this.createChip({ type: 'segment', value: segment }, label));
    });

    if (!isDefaultDealTypeSelection(filter.dealTypes)) {
      filter.dealTypes?.forEach((dealType) => {
        const label = this.resolveDealTypeLabel(dealType);
        chips.push(this.createChip({ type: 'dealType', value: dealType }, label));
      });
    }

    filter.makeIds?.forEach((makeId) => {
      const label = context.makeNames?.[makeId] ?? 'Marca';
      chips.push(this.createChip({ type: 'make', value: makeId }, label));
    });

    filter.modelIds?.forEach((modelId) => {
      const label = context.modelNames?.[modelId] ?? 'Modelo';
      chips.push(this.createChip({ type: 'model', value: modelId }, label));
    });

    filter.cityIds?.forEach((cityId, index) => {
      chips.push(
        this.createChip(
          { type: 'city', value: cityId },
          this.resolveCityLabel(filter, index),
        ),
      );
    });

    filter.ufs?.forEach((uf, index) => {
      const label = filter.ufsNames?.[index] ?? uf;
      chips.push(this.createChip({ type: 'uf', value: uf }, label));
    });

    filter.fuels?.forEach((fuel) => {
      chips.push(this.createChip({ type: 'fuel', value: fuel }, this.resolveFuelLabel(fuel)));
    });

    filter.transmissions?.forEach((transmission) => {
      chips.push(
        this.createChip(
          { type: 'transmission', value: transmission },
          this.resolveTransmissionLabel(transmission),
        ),
      );
    });

    const modelYearLabel = this.buildModelYearLabel(filter.modelYearStart, filter.modelYearEnd);
    if (modelYearLabel) {
      chips.push(this.createChip({ type: 'modelYear' }, modelYearLabel));
    }

    const priceLabel = this.buildPriceLabel(filter.priceStart, filter.priceEnd);
    if (priceLabel) {
      chips.push(this.createChip({ type: 'price' }, priceLabel));
    }

    const kmLabel = this.buildKmLabel(filter.kmStart, filter.kmEnd);
    if (kmLabel) {
      chips.push(this.createChip({ type: 'km' }, kmLabel));
    }

    const fipeLabel = this.buildFipeLabel(filter.fipeDiffStart, filter.fipeDiffEnd);
    if (fipeLabel) {
      chips.push(this.createChip({ type: 'fipe' }, fipeLabel));
    }

    filter.oemDealerIds?.forEach((oemDealerId) => {
      const label = context.oemDealerNames?.[oemDealerId] ?? 'Anunciante';
      chips.push(this.createChip({ type: 'oemDealer', value: oemDealerId }, label));
    });

    if (filter.withDossier) {
      chips.push(
        this.createChip(
          { type: 'withDossier' },
          EXCHANGE_FILTER_DIFFERENTIAL_LABELS.withDossier,
        ),
      );
    }

    if (filter.withReport) {
      chips.push(
        this.createChip(
          { type: 'withReport' },
          EXCHANGE_FILTER_DIFFERENTIAL_LABELS.withReport,
        ),
      );
    }

    filter.reportStatuses?.forEach((status) => {
      chips.push(
        this.createChip(
          { type: 'reportStatus', value: status },
          formatReportStatusLabel(status),
        ),
      );
    });

    if (filter.extraKey) {
      chips.push(
        this.createChip(
          { type: 'extraKey' },
          EXCHANGE_FILTER_DIFFERENTIAL_LABELS.extraKey,
        ),
      );
    }

    if (filter.carManual) {
      chips.push(
        this.createChip(
          { type: 'carManual' },
          EXCHANGE_FILTER_DIFFERENTIAL_LABELS.carManual,
        ),
      );
    }

    filter.equipments?.forEach((equipmentId) => {
      const label = context.equipmentNames?.[equipmentId] ?? 'Opcional';
      chips.push(this.createChip({ type: 'equipment', value: equipmentId }, label));
    });

    const publishedPeriod = resolvePublishedPeriodKey(filter.firstPublishedAtIni);
    if (publishedPeriod) {
      chips.push(
        this.createChip(
          { type: 'publishedPeriod' },
          getPublishedPeriodLabel(publishedPeriod),
        ),
      );
    }

    if (EXCHANGE_FILTER_BUY_NOW_SUPPORTED && filter.onlyWithFinalBid) {
      chips.push(this.createChip({ type: 'onlyWithFinalBid' }, 'Compre já!'));
    }

    if (filter.almostNew) {
      chips.push(this.createChip({ type: 'almostNew' }, 'Seminovos'));
    }

    if (filter.used) {
      chips.push(this.createChip({ type: 'used' }, 'Usados'));
    }

    return chips;
  }

  buildFilterDetailItems(
    filter: VehicleFilter,
    context: ActiveFilterLabelContext = {},
    options: FilterDetailBuildOptions = {},
  ): FilterDetailItem[] {
    if (!filter) {
      return [];
    }

    const items: FilterDetailItem[] = [];

    this.addFilterDetailItem(items, 'Buscar veículo', filter.searchQuery?.trim());

    this.addFilterDetailItem(
      items,
      'Tipo de veículo',
      this.joinLabels(
        (filter.segments ?? []).map((segment) => this.resolveSegmentLabel(segment)),
      ),
    );

    if (!options.hideOemDealerType) {
      this.addFilterDetailItem(
        items,
        'Tipo de anunciante',
        this.joinLabels(
          (filter.oemDealerTypes ?? []).map(
            (type) => OemDealerType[type as keyof typeof OemDealerType] ?? type,
          ),
        ),
      );
    }

    this.addFilterDetailItem(
      items,
      'Tipo de negociação',
      isDefaultDealTypeSelection(filter.dealTypes)
        ? undefined
        : this.joinLabels(
            (filter.dealTypes ?? []).map((dealType) => this.resolveDealTypeLabel(dealType)),
          ),
    );

    this.addFilterDetailItem(
      items,
      'Cidade',
      this.buildCityDetailValue(filter, options.legacyCityLabel),
    );

    this.addFilterDetailItem(
      items,
      'UF',
      this.joinLabels(
        (filter.ufs ?? []).map((uf, index) => filter.ufsNames?.[index] ?? uf),
      ) ?? filter.uf,
    );

    this.addFilterDetailItem(
      items,
      'Anunciante',
      this.joinLabels(this.resolveOemDealerLabels(filter, context)),
    );

    this.addFilterDetailItem(
      items,
      'Marcas',
      this.joinLabels(
        (filter.makeIds ?? []).map((makeId) => context.makeNames?.[makeId] ?? makeId),
      ),
    );

    this.addFilterDetailItem(
      items,
      'Modelos',
      this.joinLabels(
        (filter.modelIds ?? []).map((modelId) => context.modelNames?.[modelId] ?? modelId),
      ),
    );

    this.addFilterDetailItem(
      items,
      'Ano modelo',
      this.buildModelYearLabel(filter.modelYearStart, filter.modelYearEnd),
    );

    this.addFilterDetailItem(
      items,
      'Preço',
      this.buildPriceLabel(filter.priceStart, filter.priceEnd),
    );

    this.addFilterDetailItem(
      items,
      'Km',
      this.buildKmLabel(filter.kmStart, filter.kmEnd),
    );

    this.addFilterDetailItem(
      items,
      '% da FIPE',
      this.buildFipeLabel(filter.fipeDiffStart, filter.fipeDiffEnd),
    );

    this.addFilterDetailItem(
      items,
      'Diferenciais repasse',
      this.joinLabels(this.buildDifferentialLabels(filter)),
    );

    this.addFilterDetailItem(
      items,
      'Combustível',
      this.joinLabels((filter.fuels ?? []).map((fuel) => this.resolveFuelLabel(fuel))),
    );

    this.addFilterDetailItem(
      items,
      'Transmissão',
      this.joinLabels(
        (filter.transmissions ?? []).map((transmission) =>
          this.resolveTransmissionLabel(transmission),
        ),
      ),
    );

    this.addFilterDetailItem(
      items,
      'Opcionais',
      this.joinLabels(
        (filter.equipments ?? []).map(
          (equipmentId) => context.equipmentNames?.[equipmentId] ?? equipmentId,
        ),
      ),
    );

    const publishedPeriod = resolvePublishedPeriodKey(filter.firstPublishedAtIni);
    this.addFilterDetailItem(
      items,
      'Publicado há',
      publishedPeriod ? getPublishedPeriodLabel(publishedPeriod) : undefined,
    );

    this.addFilterDetailItem(
      items,
      'Condição',
      this.joinLabels(
        [
          filter.almostNew ? 'Seminovos' : undefined,
          filter.used ? 'Usados' : undefined,
        ].filter((label): label is string => !!label),
      ),
    );

    this.addFilterDetailItem(
      items,
      'Regras',
      EXCHANGE_FILTER_BUY_NOW_SUPPORTED && filter.onlyWithFinalBid ? 'Compre já!' : undefined,
    );

    return items;
  }

  resolveLabelContext(
    filter: VehicleFilter,
    knownContext: ActiveFilterLabelContext = {},
  ): Observable<ActiveFilterLabelContext> {
    const makeIds = (filter.makeIds ?? []).filter((id) => !knownContext.makeNames?.[id]);
    const modelIds = (filter.modelIds ?? []).filter((id) => !knownContext.modelNames?.[id]);
    const equipmentIds = (filter.equipments ?? []).filter(
      (id) => !knownContext.equipmentNames?.[id],
    );
    const oemDealerIds = this.resolveOemDealerIds(filter).filter(
      (id) => !knownContext.oemDealerNames?.[id],
    );

    if (
      makeIds.length === 0 &&
      modelIds.length === 0 &&
      equipmentIds.length === 0 &&
      oemDealerIds.length === 0
    ) {
      return of(knownContext);
    }

    return forkJoin({
      makes: makeIds.length ? this.openService.getMakes(makeIds) : of([]),
      models: modelIds.length ? this.openService.getModels(modelIds) : of([]),
      equipments: equipmentIds.length ? this.openService.getEquipments(equipmentIds) : of([]),
      oemDealers: oemDealerIds.length
        ? forkJoin(oemDealerIds.map((dealerId) => this.openService.getOemDealer(dealerId)))
        : of([]),
    }).pipe(
      map(({ makes, models, equipments, oemDealers }) => ({
        makeNames: {
          ...knownContext.makeNames,
          ...this.toNameMap(makes),
        },
        modelNames: {
          ...knownContext.modelNames,
          ...this.toNameMap(models),
        },
        equipmentNames: {
          ...knownContext.equipmentNames,
          ...this.toNameMap(equipments),
        },
        oemDealerNames: {
          ...knownContext.oemDealerNames,
          ...this.toNameMap(
            oemDealers.map((dealer) => ({
              id: String(dealer.id),
              name: dealer.name,
            })),
          ),
        },
      })),
    );
  }

  resolveFilterDetailItems(
    filter: VehicleFilter,
    knownContext: ActiveFilterLabelContext = {},
    options: FilterDetailBuildOptions = {},
  ): Observable<FilterDetailItem[]> {
    return this.resolveLabelContext(filter, knownContext).pipe(
      switchMap((context) => {
        if (filter.cityIds?.length || !filter.cityId) {
          return of(
            this.buildFilterDetailItems(filter, context, options),
          );
        }

        return this.openService.getCity(filter.cityId).pipe(
          map((city) =>
            this.buildFilterDetailItems(filter, context, {
              ...options,
              legacyCityLabel: `${city.name} - ${city.uf}`,
            }),
          ),
        );
      }),
    );
  }

  removeActiveFilter(filter: VehicleFilter, chip: ActiveFilterChip): void {
    switch (chip.key.type) {
      case 'search':
        filter.searchQuery = undefined;
        break;
      case 'segment':
        filter.segments = this.removeFromArray(filter.segments, chip.key.value);
        break;
      case 'dealType':
        filter.dealTypes = this.removeFromArray(filter.dealTypes, chip.key.value);
        break;
      case 'make':
        filter.makeIds = this.removeFromArray(filter.makeIds, chip.key.value);
        break;
      case 'model':
        filter.modelIds = this.removeFromArray(filter.modelIds, chip.key.value);
        break;
      case 'city':
        this.removeCity(filter, chip.key.value);
        break;
      case 'uf':
        filter.ufs = this.removeFromArray(filter.ufs, chip.key.value);
        filter.ufsNames = this.removeNameByIndex(filter.ufs, filter.ufsNames, chip.key.value);
        break;
      case 'fuel':
        filter.fuels = this.removeFromArray(filter.fuels, chip.key.value);
        break;
      case 'transmission':
        filter.transmissions = this.removeFromArray(filter.transmissions, chip.key.value);
        break;
      case 'modelYear':
        filter.modelYearStart = undefined;
        filter.modelYearEnd = undefined;
        break;
      case 'price':
        filter.priceStart = undefined;
        filter.priceEnd = undefined;
        break;
      case 'km':
        filter.kmStart = undefined;
        filter.kmEnd = undefined;
        break;
      case 'fipe':
        filter.fipeDiffStart = undefined;
        filter.fipeDiffEnd = undefined;
        break;
      case 'oemDealer':
        filter.oemDealerIds = this.removeFromArray(filter.oemDealerIds, chip.key.value);
        break;
      case 'withDossier':
        filter.withDossier = false;
        break;
      case 'withReport':
        filter.withReport = false;
        break;
      case 'reportStatus':
        filter.reportStatuses = this.removeFromArray(filter.reportStatuses, chip.key.value);
        break;
      case 'extraKey':
        filter.extraKey = false;
        break;
      case 'carManual':
        filter.carManual = false;
        break;
      case 'equipment':
        filter.equipments = this.removeFromArray(filter.equipments, chip.key.value);
        break;
      case 'publishedPeriod':
        filter.firstPublishedAtIni = undefined;
        break;
      case 'onlyWithFinalBid':
        filter.onlyWithFinalBid = false;
        break;
      case 'almostNew':
        filter.almostNew = false;
        break;
      case 'used':
        filter.used = false;
        break;
    }
  }

  clearActiveFiltersInPlace(filter: VehicleFilter, scope: ExchangeFilterPageScope): void {
    const pageControl = filter.pageControl;
    const gridMode = filter.gridMode;
    const previousStatuses = filter.statuses ? [...filter.statuses] : undefined;
    const previousFlags = {
      onlyFavorites: filter.onlyFavorites,
      onlyWithMyBids: filter.onlyWithMyBids,
      boughtByMe: filter.boughtByMe,
      myVehicles: filter.myVehicles,
    };

    Object.keys(filter).forEach((key) => {
      if (key !== 'pageControl' && key !== 'gridMode' && key !== 'oemDealerId') {
        delete (filter as unknown as Record<string, unknown>)[key];
      }
    });

    filter.pageControl = pageControl;
    filter.gridMode = gridMode;
    filter.statuses = 'statuses' in scope ? [...(scope.statuses ?? [])] : previousStatuses;
    filter.onlyFavorites = scope.onlyFavorites ?? previousFlags.onlyFavorites;
    filter.onlyWithMyBids = scope.onlyWithMyBids ?? previousFlags.onlyWithMyBids;
    filter.boughtByMe = scope.boughtByMe ?? previousFlags.boughtByMe;
    filter.myVehicles = scope.myVehicles ?? previousFlags.myVehicles;

    if ('dealTypes' in scope) {
      filter.dealTypes = [...(scope.dealTypes ?? [])];
    }
  }

  private createChip(key: ActiveFilterChipKey, label: string): ActiveFilterChip {
    const value = 'value' in key ? key.value : key.type;
    return {
      id: `${key.type}:${value}`,
      label,
      key,
    };
  }

  private resolveSegmentLabel(segment: string): string {
    return SegmentLabel.getSegment(segment)?.segmentLabel ?? segment;
  }

  private resolveDealTypeLabel(dealType: string): string {
    return DealTypeEnum[dealType as keyof typeof DealTypeEnum] ?? dealType;
  }

  private resolveFuelLabel(fuel: string): string {
    return FuelEnum[fuel as keyof typeof FuelEnum] ?? fuel;
  }

  private resolveTransmissionLabel(transmission: string): string {
    return TransmissionEnum[transmission as keyof typeof TransmissionEnum] ?? transmission;
  }

  private resolveCityLabel(filter: VehicleFilter, index: number): string {
    const cityName = filter.cityNames?.[index];
    const uf = filter.ufsNames?.[index];
    const cityId = filter.cityIds?.[index];

    if (cityName && uf) {
      return `${cityName} - ${uf}`;
    }

    return cityName ?? cityId ?? 'Cidade';
  }

  private buildModelYearLabel(
    start: number | undefined,
    end: number | undefined,
  ): string | undefined {
    if (start !== undefined && end !== undefined) {
      return `de ${start} a ${end}`;
    }

    if (start !== undefined) {
      return `a partir de ${start}`;
    }

    if (end !== undefined) {
      return `até ${end}`;
    }

    return undefined;
  }

  private buildPriceLabel(
    start: number | undefined,
    end: number | undefined,
  ): string | undefined {
    if (start !== undefined && end !== undefined) {
      return `de R$ ${this.formatNumber(start)} a R$ ${this.formatNumber(end)}`;
    }

    if (start !== undefined) {
      return `a partir de R$ ${this.formatNumber(start)}`;
    }

    if (end !== undefined) {
      return `até R$ ${this.formatNumber(end)}`;
    }

    return undefined;
  }

  private buildKmLabel(start: number | undefined, end: number | undefined): string | undefined {
    if (start !== undefined && end !== undefined) {
      return `de ${this.formatNumber(start)} km a ${this.formatNumber(end)} km`;
    }

    if (start !== undefined) {
      return `a partir de ${this.formatNumber(start)} km`;
    }

    if (end !== undefined) {
      return `até ${this.formatNumber(end)} km`;
    }

    return undefined;
  }

  private buildFipeLabel(
    fipeDiffStart: number | undefined,
    fipeDiffEnd: number | undefined,
  ): string | undefined {
    if (fipeDiffStart === undefined && fipeDiffEnd === undefined) {
      return undefined;
    }

    const start = Math.round((fipeDiffStart ?? fipeDiffEnd ?? 0) * 100);
    const end = Math.round((fipeDiffEnd ?? fipeDiffStart ?? 0) * 100);
    return `de ${start}% a ${end}%`;
  }

  private buildDifferentialLabels(filter: VehicleFilter): string[] {
    const labels: string[] = [];

    if (filter.withDossier) {
      labels.push(EXCHANGE_FILTER_DIFFERENTIAL_LABELS.withDossier);
    }

    if (filter.withReport) {
      labels.push(EXCHANGE_FILTER_DIFFERENTIAL_LABELS.withReport);
    }

    filter.reportStatuses?.forEach((status) => {
      labels.push(formatReportStatusLabel(status));
    });

    if (filter.extraKey) {
      labels.push(EXCHANGE_FILTER_DIFFERENTIAL_LABELS.extraKey);
    }

    if (filter.carManual) {
      labels.push(EXCHANGE_FILTER_DIFFERENTIAL_LABELS.carManual);
    }

    return labels;
  }

  private buildCityDetailValue(
    filter: VehicleFilter,
    legacyCityLabel?: string,
  ): string | undefined {
    const labels = (filter.cityIds ?? []).map((cityId, index) =>
      this.resolveCityLabel(filter, index) ?? cityId,
    );

    if (!labels.length && legacyCityLabel) {
      labels.push(legacyCityLabel);
    }

    if (!labels.length) {
      return undefined;
    }

    let result = this.joinLabels(labels) ?? '';
    if (filter.cityRadius && filter.cityRadius > 0) {
      result += ` + ${this.formatNumber(filter.cityRadius)} km`;
    }

    return result;
  }

  private resolveOemDealerIds(filter: VehicleFilter): string[] {
    return [
      ...(filter.oemDealerIds ?? []),
      ...(filter.oemDealerId ? [String(filter.oemDealerId)] : []),
    ].filter((id, index, ids) => ids.indexOf(id) === index);
  }

  private resolveOemDealerLabels(
    filter: VehicleFilter,
    context: ActiveFilterLabelContext,
  ): string[] {
    return this.resolveOemDealerIds(filter).map(
      (dealerId) => context.oemDealerNames?.[dealerId] ?? dealerId,
    );
  }

  private joinLabels(labels: string[]): string | undefined {
    const normalized = labels.filter((label) => label.length > 0);
    return normalized.length > 0 ? normalized.join(', ') : undefined;
  }

  private addFilterDetailItem(
    items: FilterDetailItem[],
    id: string,
    value: string | undefined,
  ): void {
    if (!value?.length) {
      return;
    }

    items.push({ id, name: value });
  }

  private formatNumber(value: number): string {
    return this.decimalPipe.transform(value, '1.0-0') ?? String(value);
  }

  private removeFromArray<T>(values: T[] | undefined, value: T): T[] {
    return (values ?? []).filter((item) => item !== value);
  }

  private removeCity(filter: VehicleFilter, cityId: string): void {
    const index = filter.cityIds?.indexOf(cityId) ?? -1;
    if (index < 0) {
      return;
    }

    filter.cityIds = filter.cityIds?.filter((id) => id !== cityId);
    filter.cityNames = filter.cityNames?.filter((_, itemIndex) => itemIndex !== index);
    filter.ufsNames = filter.ufsNames?.filter((_, itemIndex) => itemIndex !== index);

    if ((filter.cityIds?.length ?? 0) === 0) {
      filter.cityRadius = undefined;
    }
  }

  private removeNameByIndex(
    values: string[] | undefined,
    names: string[] | undefined,
    value: string,
  ): string[] | undefined {
    const index = values?.indexOf(value) ?? -1;
    if (index < 0 || !names) {
      return names;
    }

    return names.filter((_, itemIndex) => itemIndex !== index);
  }

  private toNameMap(items: Array<{ id: string; name: string }>): Record<string, string> {
    return items.reduce<Record<string, string>>((accumulator, item) => {
      accumulator[item.id] = item.name;
      return accumulator;
    }, {});
  }
}
